import { Big } from 'big.js'
import { Measure } from '../types'


enum EnergyMeasurementSystem {
  siUnits = 'SI Units',
  equivalentUnits = 'Equivalent Units',
  foodEnergy = 'Food Energy Units',
  mechanicalEnergy = 'Mechanical Energy Units',
  explosiveEnergy = 'Explosive Energy Units'
}

const energyUnits = {
  joule: {
    displayName: 'Joule',
    codeName: 'joule',
    url: 'joule',
    symbol: 'J',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: EnergyMeasurementSystem.siUnits
  },
  kilojoule: {
    displayName: 'Kilojoule',
    codeName: 'kilojoule',
    url: 'kilojoule',
    symbol: 'kJ',
    toBaseUnit: (value: Big): Big => value.times(1000),
    fromBaseUnit: (value: Big): Big => value.div(1000),
    system: EnergyMeasurementSystem.siUnits
  },
  megajoule: {
    displayName: 'Megajoule',
    codeName: 'megajoule',
    url: 'megajoule',
    symbol: 'MJ',
    toBaseUnit: (value: Big): Big => value.times(1000000),
    fromBaseUnit: (value: Big): Big => value.div(1000000),
    system: EnergyMeasurementSystem.siUnits
  },
  gigajoule: {
    displayName: 'Gigajoule',
    codeName: 'gigajoule',
    url: 'gigajoule',
    symbol: 'GJ',
    toBaseUnit: (value: Big): Big => value.times(1000000000),
    fromBaseUnit: (value: Big): Big => value.div(1000000000),
    system: EnergyMeasurementSystem.siUnits
  },


  barrels_of_oil_equivalent: {
    displayName: 'Barrels of Oil Equivalent',
    codeName: 'barrels_of_oil_equivalent',
    url: 'barrels-of-oil-equivalent',
    symbol: 'BOE',
    toBaseUnit: (value: Big): Big => value.times(6118000000),
    fromBaseUnit: (value: Big): Big => value.div(6118000000),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  british_thermal_units: {
    displayName: 'British Thermal Units',
    codeName: 'british_thermal_units',
    url: 'british-thermal-units',
    symbol: 'BTU',
    toBaseUnit: (value: Big): Big => value.times(1055.0558526),
    fromBaseUnit: (value: Big): Big => value.div(1055.0558526),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  electron_volt: {
    displayName: 'Electron Volt',
    codeName: 'electron_volt',
    url: 'electron-volt',
    symbol: 'eV',
    toBaseUnit: (value: Big): Big => value.times(1.602176634e-19),
    fromBaseUnit: (value: Big): Big => value.div(1.602176634e-19),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  erg: {
    displayName: 'Erg',
    codeName: 'erg',
    url: 'erg',
    symbol: 'erg',
    toBaseUnit: (value: Big): Big => value.times(1e-7),
    fromBaseUnit: (value: Big): Big => value.div(1e-7),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  therm_ec: {
    displayName: 'Therm (EC)',
    codeName: 'therm_ec',
    url: 'therm-ec',
    symbol: 'thm-ec',
    toBaseUnit: (value: Big): Big => value.times(105505600),
    fromBaseUnit: (value: Big): Big => value.div(105505600),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  therm_us: {
    displayName: 'Therm (US)',
    codeName: 'therm_us',
    url: 'therm-us',
    symbol: 'thm-us',
    toBaseUnit: (value: Big): Big => value.times(105480400),
    fromBaseUnit: (value: Big): Big => value.div(105480400),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  watt_hour: {
    displayName: 'Watt Hour',
    codeName: 'watt_hour',
    url: 'watt-hour',
    symbol: 'Wh',
    toBaseUnit: (value: Big): Big => value.times(3600),
    fromBaseUnit: (value: Big): Big => value.div(3600),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  megawatt_hour: {
    displayName: 'Megawatt Hour',
    codeName: 'megawatt_hour',
    url: 'megawatt-hour',
    symbol: 'MWh',
    toBaseUnit: (value: Big): Big => value.times(3600000000),
    fromBaseUnit: (value: Big): Big => value.div(3600000000),
    system: EnergyMeasurementSystem.equivalentUnits
  },
  kilowatt_hour: {
    displayName: 'Kilowatt Hour',
    codeName: 'kilowatt_hour',
    url: 'kilowatt-hour',
    symbol: 'kWh',
    toBaseUnit: (value: Big): Big => value.times(3600000),
    fromBaseUnit: (value: Big): Big => value.div(3600000),
    system: EnergyMeasurementSystem.equivalentUnits
  },


  calorie_th: {
    displayName: 'Calorie (th)',
    codeName: 'calorie_th',
    url: 'calorie-th',
    symbol: 'cal (th)',
    toBaseUnit: (value: Big): Big => value.times(4.184),
    fromBaseUnit: (value: Big): Big => value.div(4.184),
    system: EnergyMeasurementSystem.foodEnergy

  },
  calorie_nutritional: {
    displayName: 'Calorie (nutritional)',
    codeName: 'calorie_nutritional',
    url: 'nutritional-calorie',
    symbol: 'cal',
    toBaseUnit: (value: Big): Big => value.times(4.1868),
    fromBaseUnit: (value: Big): Big => value.div(4.1868),
    system: EnergyMeasurementSystem.foodEnergy
  },
  kilocalorie_th: {
    displayName: 'Kilocalorie (th)',
    codeName: 'kilocalorie_th',
    url: 'kilocalorie-th',
    symbol: 'kcal (th)',
    toBaseUnit: (value: Big): Big => value.times(4184),
    fromBaseUnit: (value: Big): Big => value.div(4184),
    system: EnergyMeasurementSystem.foodEnergy
  },
  kilocalorie_nutritional: {
    displayName: 'Kilocalorie (nutritional)',
    codeName: 'kilocalorie_nutritional',
    url: 'nutritional-kilocalorie',
    symbol: 'kcal',
    toBaseUnit: (value: Big): Big => value.times(4186.8),
    fromBaseUnit: (value: Big): Big => value.div(4186.8),
    system: EnergyMeasurementSystem.foodEnergy
  },

  inch_pound: {
    displayName: 'Inch-Pound',
    codeName: 'inch_pound',
    url: 'inch-pound',
    symbol: 'in·lb',
    toBaseUnit: (value: Big): Big => value.times(0.1129848290276167),
    fromBaseUnit: (value: Big): Big => value.div(0.1129848290276167),
    system: EnergyMeasurementSystem.mechanicalEnergy
  },
  inch_ounce: {
    displayName: 'Inch-Ounce',
    codeName: 'inch_ounce',
    url: 'inch-ounce',
    symbol: 'in-oz',
    toBaseUnit: (value: Big): Big => value.times(0.007061551814226043),
    fromBaseUnit: (value: Big): Big => value.div(0.007061551814226043),
    system: EnergyMeasurementSystem.mechanicalEnergy
  },
  foot_pound: {
    displayName: 'Foot-pound',
    codeName: 'foot_pound',
    url: 'foot-pound',
    symbol: 'ft-lb',
    toBaseUnit: (value: Big): Big => value.times(new Big('1.3558179483314004')),
    fromBaseUnit: (value: Big): Big => value.div(new Big('1.3558179483314004')),
    system: EnergyMeasurementSystem.mechanicalEnergy
  },

  grams_of_TNT: {
    displayName: 'Grams of TNT',
    codeName: 'grams_of_TNT',
    url: 'grams-of-tnt',
    symbol: 'gTNT',
    toBaseUnit: (value: Big): Big => value.times(4184),
    fromBaseUnit: (value: Big): Big => value.div(4184),
    system: EnergyMeasurementSystem.explosiveEnergy
  },
  kilograms_of_TNT: {
    displayName: 'Kilograms of TNT',
    codeName: 'kilograms_of_TNT',
    url: 'kilograms-of-tnt',
    symbol: 'kgTNT',
    toBaseUnit: (value: Big): Big => value.times(4184000),
    fromBaseUnit: (value: Big): Big => value.div(4184000),
    system: EnergyMeasurementSystem.explosiveEnergy
  },
  tons_of_TNT: {
    displayName: 'Tons of TNT',
    codeName: 'tons_of_TNT',
    url: 'tons-of-tnt',
    symbol: 'tTNT',
    toBaseUnit: (value: Big): Big => value.times(4184000000),
    fromBaseUnit: (value: Big): Big => value.div(4184000000),
    system: EnergyMeasurementSystem.explosiveEnergy
  },
  megatons_of_TNT: {
    displayName: 'Megatons of TNT',
    codeName: 'megatons_of_TNT',
    url: 'megatons-of-tnt',
    symbol: 'MtTNT',
    toBaseUnit: (value: Big): Big => value.times(4.184e+15),
    fromBaseUnit: (value: Big): Big => value.div(4.184e+15),
    system: EnergyMeasurementSystem.explosiveEnergy
  }
}

export const energy: Measure = {
  displayName: 'Energy',
  codeName: 'energy',
  url: 'energy',
  units: energyUnits,
  baseUnit: energyUnits.joule,
  defaultFromUnit: energyUnits.kilojoule,
  defaultToUnit: energyUnits.joule
}
